import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
import { getURLQueries } from "../../../../utils/helper";
import { RCL as useTranslation } from "../../../../components/RCL";
import { Icon } from "../../../../components/Icons";
import { OktaSignInWidgetIE } from ".";
import * as styles from "./LayoutWrapper.module.scss";

const cx = classNames.bind(styles);

const LayoutWrapper = ({ language, isAction, setIsOpen, setLoginOpen, setUserDismiss }) => {
	const imageTranslation = useTranslation({
		searchKey: "okta-widget-become-member-image",
	}).split("||");

	const dictionary = {
		"okta-widget-become-member-title-v2": useTranslation({
			searchKey: "okta-widget-become-member-title-v2",
		}),
		// SSO related content updates
		"okta-widget-become-member-access-your-booking-heading": useTranslation({
			searchKey: "okta-widget-become-member-access-your-booking-heading",
		}),
		"okta-widget-become-member-access-your-booking-message": useTranslation({
			searchKey: "okta-widget-become-member-access-your-booking-message",
		}),
		"okta-widget-become-member-member-perks-heading": useTranslation({
			searchKey: "okta-widget-become-member-member-perks-heading",
		}),
		"okta-widget-become-member-member-perks-message": useTranslation({
			searchKey: "okta-widget-become-member-member-perks-message",
		}),
		"okta-widget-become-member-f&f-heading": useTranslation({
			searchKey: "okta-widget-become-member-f&f-heading",
		}),
		"okta-widget-become-member-f&f-message": useTranslation({
			searchKey: "okta-widget-become-member-f&f-message",
		}),
		"okta-widget-become-member-f&f-heading-desc": useTranslation({
			searchKey: "okta-widget-become-member-f&f-heading-desc",
		}),
		"modal-close": useTranslation({
			searchKey: "modal-close",
		}),
	};

	const oktaAction = getURLQueries("type_hint");
	const oktaSession = getURLQueries("session_hint");
	const [newAccountFlag, setNewAccountFlag] = useState(false);
	const isAccountCreated = oktaAction === "ACTIVATION" && oktaSession === "AUTHENTICATED";
	const [isSignIn, setSignIn] = useState(false);
	const [viewportHeight, setViewportHeight] = useState(null);

	const handleResize = () => {
		const windowWidth = window.innerWidth || document.documentElement.clientWidth;

		// Continue tracking viewport width with fallback
		if (windowWidth <= 575) {
			setViewportHeight(window.innerHeight || document.documentElement.clientHeight);
		} else {
			setViewportHeight(null);
		}
	};

	useEffect(() => {
		if (!newAccountFlag) {
			setNewAccountFlag(isAccountCreated);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Resize event
		window.addEventListener("resize", handleResize, false);

		// Init to display desktop form
		handleResize();

		return () => {
			window.removeEventListener("resize", handleResize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={cx("widgetLayout")} style={{ height: viewportHeight }}>
			<div className={cx("widgetValue")}>
				<div className={cx("featureContainer")}>
					<img
						className={cx("featureImage")}
						src={
							imageTranslation[0].indexOf("https") > -1
								? imageTranslation[0]
								: "https://via.placeholder.com/1200x675"
						}
						alt={imageTranslation[1] ? imageTranslation[1] : ""}
					/>
					<div className={cx("featureHeading")}>
						{dictionary["okta-widget-become-member-title-v2"]}
					</div>
				</div>
				<div className={cx("featureMemberContainer")}>
					<div className={cx("featureMemberItem")}>
						<div className={cx("featureIcon", "alignTop")}>
							<Icon className={cx("svgIcon")} name="add-trip--o" aria-hidden="true" />
						</div>
						<div className={cx("featureBullet")}>
							<div className={cx("title")}>
								{dictionary["okta-widget-become-member-access-your-booking-heading"]}
							</div>
							<div className={cx("desc")}>
								{dictionary["okta-widget-become-member-access-your-booking-message"]}
							</div>
						</div>
					</div>

					<div className={cx("featureMemberItem")}>
						<div className={cx("featureIcon", "alignTop")}>
							<Icon className={cx("svgIcon")} name="member-perks--o" aria-hidden="true" />
						</div>
						<div className={cx("featureBullet")}>
							<div className={cx("title")}>
								{dictionary["okta-widget-become-member-member-perks-heading"]}
							</div>
							<div className={cx("desc")}>
								{dictionary["okta-widget-become-member-member-perks-message"]}
							</div>
						</div>
					</div>

					<div className={cx("featureMemberItem")}>
						<div className={cx("featureIcon", "alignTop")}>
							<Icon className={cx("svgIcon")} name="friends-and-family" aria-hidden="true" />
						</div>
						<div className={cx("featureBullet")}>
							<div className={cx("title")}>
								{dictionary["okta-widget-become-member-f&f-heading"]}
							</div>
							<div className={cx("desc")}>
								{dictionary["okta-widget-become-member-f&f-heading-desc"]}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className={cx("widgetWrapper")}>
				{!newAccountFlag && !isSignIn && (
					<div className={cx("featureWrapper")}>
						<div className={cx("featureContainer")}>
							<div className={cx("featureHeading")}>
								{dictionary["okta-widget-become-member-title-v2"]}
							</div>
							<div className={cx("closeMobile")}>
								<button
									type="button"
									aria-label={dictionary["modal-close"]}
									onClick={() => {
										setIsOpen(false);
										setLoginOpen(false);
										setUserDismiss(true);
									}}>
									<Icon name="close--n" aria-hidden="true" />
								</button>
							</div>
						</div>
						<div className={cx("featureList")}>
							<div className={cx("featureItem")}>
								<div className={cx("icon")}>
									<Icon className={cx("svgIcon")} name="add-trip--o" aria-hidden="true" />
								</div>
								<div className={cx("content")}>
									{dictionary["okta-widget-become-member-access-your-booking-heading"]}
								</div>
							</div>
							<div className={cx("featureItem")}>
								<div className={cx("icon")}>
									<Icon className={cx("svgIcon")} name="member-perks--o" aria-hidden="true" />
								</div>
								<div className={cx("content")}>
									{dictionary["okta-widget-become-member-member-perks-heading"]}
								</div>
							</div>
							<div className={cx("featureItem")}>
								<div className={cx("icon")}>
									<Icon className={cx("svgIcon")} name="friends-and-family" aria-hidden="true" />
								</div>
								<div className={cx("content")}>
									{dictionary["okta-widget-become-member-f&f-heading"]}
								</div>
							</div>
						</div>
					</div>
				)}
				<OktaSignInWidgetIE
					lang={language}
					isAccountCreated={isAccountCreated || newAccountFlag}
					isActionDefault={isAction}
					setIsOpen={setIsOpen}
					setLoginOpen={setLoginOpen}
					setUserDismiss={setUserDismiss}
					isSignIn={isSignIn}
					setSignIn={setSignIn}
					className="oie"
				/>
			</div>
		</div>
	);
};

LayoutWrapper.propTypes = {
	language: PropTypes.oneOf(["en", "fr"]).isRequired,
	isAction: PropTypes.string.isRequired,
	setIsOpen: PropTypes.func.isRequired,
	setLoginOpen: PropTypes.func.isRequired,
	setUserDismiss: PropTypes.func.isRequired,
};

export default LayoutWrapper;
